import {useCallback, useRef, useState} from "react";
import GIF from "gif.js";
// Import the worker as a file asset so Bun emits it and gives us a same-origin
// URL. Resolving via `new URL(..., import.meta.url)` yields a file:// path under
// Bun, which the browser blocks from constructing a Worker across origins.
import gifWorker from "gif.js/dist/gif.worker.js" with {type: "file"};

import {useSetting} from "contexts/setting";
import {knownTemplatesFor} from "data/templates";
import {useAuth} from "hooks/useAuth";
import {API} from "api";
import {openErrorDialog} from "layout/ErrorDialog";
import {
  buildExportCompletionAnalytics,
  buildExportFailure,
  buildExportFailureAnalytics,
} from "libs/exportFailure";
import {track} from "libs/analytics";
import {getVideoExportFormat} from "libs/videoExportFormats";
import {fitVideoExportProfile, getVideoExportProfile} from "libs/videoExportProfiles";
import {getVideoExportSourceWarnings} from "libs/videoExportWarnings";
import {
  cleanupMediaCapture,
  finishGifCapture,
  getMediaRecorderError,
  MEDIA_RECORDER_OPTIONS,
  observeCaptureCancellation,
  renderGifWithWatchdog,
} from "libs/mediaCapture";

function warnCaptureCleanupErrors(kind, errors) {
  for (const error of errors) {
    console.warn(`[media-export] ${kind} cleanup failed`, error);
  }
}

/**
 * Build one 256-color palette that represents ALL captured frames, using
 * median-cut quantization over a pixel sample drawn across the whole sequence.
 *
 * Why: gif.js quantizes each frame to its own palette by default, so identical
 * pixels (e.g. a static logo) land on slightly different palette entries every
 * frame and flicker. Forcing a single shared palette fixes that — but deriving
 * it from frame 0 alone (gif.js `globalPalette: true`) misses the hues an
 * animated gradient drifts through later in the loop, which shows up as
 * banding. Sampling every frame yields a palette that covers the full gamut,
 * so static regions stay stable AND gradients stay smooth.
 *
 * Returns a flat [r,g,b, r,g,b, …] array of exactly 256 colors — the colorTab
 * format gif.js's worker consumes when `globalPalette` is an array.
 */
function buildGlobalPalette(frames) {
  const TARGET_SAMPLES = 40000;
  const perFrame = Math.max(1, Math.floor(TARGET_SAMPLES / frames.length));
  const samples = [];
  for (const canvas of frames) {
    const ctx = canvas.getContext("2d", {willReadFrequently: true});
    const {data} = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const pixelCount = data.length / 4;
    const stride = Math.max(1, Math.floor(pixelCount / perFrame));
    for (let p = 0; p < pixelCount; p += stride) {
      const i = p * 4;
      samples.push([data[i], data[i + 1], data[i + 2]]);
    }
  }

  // Median cut: repeatedly split the box with the widest channel range at its
  // median along that channel, until we have 256 boxes (or can't split).
  let boxes = [samples];
  while (boxes.length < 256) {
    let target = -1;
    let bestRange = 0;
    let bestChannel = 0;
    for (let b = 0; b < boxes.length; b++) {
      const box = boxes[b];
      if (box.length < 2) continue;
      const min = [255, 255, 255];
      const max = [0, 0, 0];
      for (const px of box) {
        for (let c = 0; c < 3; c++) {
          if (px[c] < min[c]) min[c] = px[c];
          if (px[c] > max[c]) max[c] = px[c];
        }
      }
      const ranges = [max[0] - min[0], max[1] - min[1], max[2] - min[2]];
      const ch =
        ranges[0] >= ranges[1] && ranges[0] >= ranges[2] ? 0 : ranges[1] >= ranges[2] ? 1 : 2;
      if (ranges[ch] > bestRange) {
        bestRange = ranges[ch];
        target = b;
        bestChannel = ch;
      }
    }
    if (target < 0 || bestRange === 0) break; // every box is a single color
    const box = boxes[target];
    box.sort((a, b) => a[bestChannel] - b[bestChannel]);
    const mid = box.length >> 1;
    boxes.splice(target, 1, box.slice(0, mid), box.slice(mid));
  }

  // Average each box to its representative color, flatten to a colorTab.
  const palette = [];
  for (const box of boxes) {
    let r = 0;
    let g = 0;
    let b = 0;
    for (const px of box) {
      r += px[0];
      g += px[1];
      b += px[2];
    }
    const n = box.length || 1;
    palette.push(Math.round(r / n), Math.round(g / n), Math.round(b / n));
  }
  // Pad to exactly 256 colors (768 bytes) by repeating the last color, so the
  // written palette has no stray black entries.
  while (palette.length < 256 * 3) {
    palette.push(
      palette[palette.length - 3] ?? 0,
      palette[palette.length - 2] ?? 0,
      palette[palette.length - 1] ?? 0
    );
  }
  return palette.slice(0, 256 * 3);
}

// 8×8 Bayer threshold matrix (values 0..63) for ordered dithering.
const BAYER_8X8 = [
  0, 32, 8, 40, 2, 34, 10, 42, 48, 16, 56, 24, 50, 18, 58, 26, 12, 44, 4, 36, 14, 46, 6, 38, 60, 28,
  52, 20, 62, 30, 54, 22, 3, 35, 11, 43, 1, 33, 9, 41, 51, 19, 59, 27, 49, 17, 57, 25, 15, 47, 7,
  39, 13, 45, 5, 37, 63, 31, 55, 23, 61, 29, 53, 21,
];

/**
 * Apply ordered (Bayer) dithering in place: nudge each pixel by a fixed,
 * position-dependent threshold before gif.js quantizes it against the shared
 * palette. This breaks up gradient banding the 256-color palette can't avoid.
 *
 * Crucially the perturbation depends only on pixel POSITION, not on neighbors,
 * so a static region (e.g. the logo) gets the exact same nudge every frame and
 * stays rock-steady. Error-diffusion dithering (gif.js's built-in `dither`)
 * would instead bleed the moving gradient's error into static regions and make
 * them flicker — which is the artifact this whole path is avoiding.
 */
function applyOrderedDither(canvas, strength) {
  const ctx = canvas.getContext("2d", {willReadFrequently: true});
  const {width, height} = canvas;
  const img = ctx.getImageData(0, 0, width, height);
  const d = img.data;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const nudge = (BAYER_8X8[(y & 7) * 8 + (x & 7)] / 64 - 0.5) * strength;
      const i = (y * width + x) * 4;
      d[i] = Math.max(0, Math.min(255, d[i] + nudge));
      d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + nudge));
      d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + nudge));
    }
  }
  ctx.putImageData(img, 0, 0);
}

export function useMediaExport() {
  const {role, featureFlags} = useAuth();
  const {
    product,
    channel,
    editIsVideoLoading,
    editIsGifLoading,
    videoDuration,
    editVideoExportCounter,
  } = useSetting();

  const streamRef = useRef(null);
  const videoFramesRef = useRef([]);
  const processorRef = useRef(null);
  const readerRef = useRef(null);
  const captureCancelledRef = useRef(false);
  // The last status of this banner's server video job; null when no job runs.
  const [serverVideoStatus, setServerVideoStatus] = useState(null);

  const reportExportFailure = useCallback(
    (kind, error) => {
      const failure = buildExportFailure(error, {
        kind,
        product: product.value,
        channel: channel.value,
      });
      console.error(`[media-export] ${kind} export failed`, failure.diagnostics);
      track("neurobaker.export.failed", buildExportFailureAnalytics(error, {role, kind}));
      openErrorDialog(failure);
    },
    [product.value, channel.value, role]
  );

  const reportExportCompleted = useCallback(
    (kind, renderer = "screen-capture") => {
      track("neurobaker.export.completed", buildExportCompletionAnalytics({role, kind, renderer}));
    },
    [role]
  );

  const generateFilename = useCallback(
    (containerId, pageType, dimensionsOverride = "") => {
      const template = knownTemplatesFor(pageType, product.value).find((t) => t.id === containerId);
      const dimensions = dimensionsOverride || template?.dimensions || "";
      const templateFilename = template?.filename || "";

      const productValue = product?.value || "unknown";
      // The same channel `pageType` looked the template up under, so the
      // name matches the image export's for the same offering.
      const channelValue = pageType || "unknown";
      const dimensionsForFilename = dimensions.replace("x", "x");

      const filename = templateFilename
        ? `${productValue}-${templateFilename}-${dimensionsForFilename}`
        : `${productValue}-${channelValue}-${containerId}-${dimensionsForFilename}`;

      return filename;
    },
    [product]
  );

  // Convert VideoFrame to canvas
  const videoFrameToCanvas = useCallback((videoFrame, targetWidth, targetHeight) => {
    const canvas = document.createElement("canvas");
    canvas.width = targetWidth || videoFrame.displayWidth;
    canvas.height = targetHeight || videoFrame.displayHeight;

    const ctx = canvas.getContext("2d", {willReadFrequently: true});
    ctx.drawImage(videoFrame, 0, 0, canvas.width, canvas.height);

    return canvas;
  }, []);

  // Start capturing frames using WebCodecs
  const startCapture = useCallback(
    async (containerId, pageType, key) => {
      if (key !== "pregif") {
        editIsVideoLoading(true);
      }
      try {
        videoFramesRef.current = [];
        captureCancelledRef.current = false;

        const container = document.querySelector(`#${containerId}`);
        if (!container) {
          throw new Error(`Export container not found: ${containerId}`);
        }

        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;

        // Get display media stream
        const cropTarget = await CropTarget.fromElement(container);
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            width: {ideal: containerWidth},
            height: {ideal: containerHeight},
            frameRate: {ideal: 30},
          },
          audio: false,
          preferCurrentTab: true,
        });
        streamRef.current = stream;

        const [videoTrack] = stream.getVideoTracks();
        observeCaptureCancellation(videoTrack, () => {
          captureCancelledRef.current = true;
        });
        await videoTrack.cropTo(cropTarget);

        console.log("✅ Crop applied to video track");

        // Use MediaStreamTrackProcessor for WebCodecs
        const processor = new MediaStreamTrackProcessor({track: videoTrack});
        processorRef.current = processor;
        const reader = processor.readable.getReader();
        readerRef.current = reader;

        console.log("🎬 Started capturing frames...");

        return {
          stream,
          processor,
          reader,
          filename: generateFilename(containerId, pageType),
        };
      } catch (error) {
        const cleanupErrors = await cleanupMediaCapture(readerRef.current, streamRef.current);
        readerRef.current = null;
        processorRef.current = null;
        streamRef.current = null;
        warnCaptureCleanupErrors("Capture", cleanupErrors);
        editIsVideoLoading(false);
        editIsGifLoading(false);
        editVideoExportCounter(0, key);
        // Closing the browser picker is an expected cancellation, not an
        // export failure that needs an error dialog.
        if (error?.name === "NotAllowedError") return null;
        throw error;
      }
    },
    [editIsVideoLoading, editIsGifLoading, editVideoExportCounter, generateFilename]
  );

  // Stop capturing and collect frames
  const stopCapture = useCallback(
    async (key) => {
      const cleanupErrors = await cleanupMediaCapture(readerRef.current, streamRef.current);
      readerRef.current = null;
      streamRef.current = null;

      processorRef.current = null;
      editIsVideoLoading(false);
      editVideoExportCounter(0, key);

      warnCaptureCleanupErrors(key, cleanupErrors);
      console.log(`✅ Captured ${videoFramesRef.current.length} frames`);
      return videoFramesRef.current;
    },
    [editIsVideoLoading, editVideoExportCounter]
  );

  // Read frames continuously
  const captureFrames = useCallback(
    async (reader, durationMs) => {
      const startTime = Date.now();
      const targetFps = 15; // Capture 15 FPS for GIF
      const frameInterval = 1000 / targetFps;
      let lastFrameTime = 0;

      let targetWidth = null;
      let targetHeight = null;

      let progressInterval;

      try {
        progressInterval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min((elapsed / durationMs) * 100, 100);
          const roundedProgress = Math.round(progress);
          console.log(`GIF capturing: ${roundedProgress}%`);
          editVideoExportCounter(roundedProgress, "pregif");
        }, 100);

        while (true) {
          const {done, value: videoFrame} = await reader.read();

          if (done) break;

          const currentTime = Date.now() - startTime;

          // Stop if duration exceeded
          if (currentTime >= durationMs) {
            videoFrame.close();
            break;
          }

          // Throttle frame capture to target FPS
          if (currentTime - lastFrameTime >= frameInterval) {
            // set the first frame size
            if (!targetWidth) {
              targetWidth = videoFrame.displayWidth;
              targetHeight = videoFrame.displayHeight;
            }

            // Clone frame to keep it alive
            const canvas = videoFrameToCanvas(videoFrame, targetWidth, targetHeight);
            videoFramesRef.current.push(canvas);
            lastFrameTime = currentTime;
          }

          // Always close the frame to free memory
          videoFrame.close();
        }
      } finally {
        if (progressInterval) {
          clearInterval(progressInterval);
          editVideoExportCounter(0, "pregif");
        }
      }
    },
    [videoFrameToCanvas, editVideoExportCounter]
  );

  // Create GIF from captured frames
  const createGifFromFrames = useCallback(
    async (frames) => {
      try {
        console.log(`🎨 Creating GIF from ${frames.length} frames...`);
        if (frames.length === 0) {
          throw new Error("No frames captured");
        }

        // Get dimensions from first frame
        const {width, height} = frames[0];

        // One shared palette sampled across every frame. gif.js quantizes all
        // frames against it (no per-frame palette), so static regions like the
        // logo stay stable while the gradient keeps its full color range.
        const globalPalette = buildGlobalPalette(frames);

        const gif = new GIF({
          workers: 2,
          quality: 3, // 1-30 (lower is better quality)
          width,
          height,
          workerScript: gifWorker,
          repeat: 0, // Loop forever
          globalPalette,
        });

        // Add all frames to GIF. Ordered-dither each frame first (palette is
        // already computed from the originals above) to smooth gradient bands
        // without making static regions flicker.
        const delay = Math.round(1000 / 15); // 15 FPS
        frames.forEach((canvas) => {
          applyOrderedDither(canvas, 28);
          gif.addFrame(canvas, {delay, copy: true});
        });

        const blob = await renderGifWithWatchdog(gif, {
          onProgress: (progress) => {
            editVideoExportCounter(Math.round(progress * 100), "gif");
            console.log(`GIF encoding: ${Math.round(progress * 100)}%`);
          },
        });
        console.log("✅ GIF created successfully");
        editVideoExportCounter(0, "gif");
        return blob;
      } catch (error) {
        editVideoExportCounter(0, "gif");
        throw error;
      }
    },
    [editVideoExportCounter]
  );

  // Create video using MediaRecorder
  const createVideoFromStream = useCallback(
    async (stream, durationMs, key) =>
      new Promise((resolve, reject) => {
        const chunks = [];
        const mediaRecorder = new MediaRecorder(stream, MEDIA_RECORDER_OPTIONS);
        const [videoTrack] = stream.getVideoTracks();
        const stopAfterShareEnds = () => {
          if (mediaRecorder.state !== "inactive") mediaRecorder.stop();
        };
        videoTrack?.addEventListener("ended", stopAfterShareEnds, {once: true});

        const startTime = Date.now();
        let progressInterval;
        let stopTimer;

        mediaRecorder.ondataavailable = (event) => {
          if (event.data.size > 0) {
            chunks.push(event.data);
          }
        };

        mediaRecorder.onstart = () => {
          console.log("🎬 Video recording started...");

          // Update progress each 100ms
          progressInterval = setInterval(() => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min((elapsed / durationMs) * 100, 100);
            console.log(`Video recording: ${Math.round(progress)}%`);
            editVideoExportCounter(Math.round(progress), key);
            //TODO visualize video progress in UI
          }, 100);
        };

        mediaRecorder.onstop = () => {
          clearInterval(progressInterval);
          clearTimeout(stopTimer);
          videoTrack?.removeEventListener("ended", stopAfterShareEnds);
          console.log("Video recording: 100%");

          const blob = new Blob(chunks, {type: "video/webm"});
          resolve(blob);
        };

        mediaRecorder.onerror = (event) => {
          clearInterval(progressInterval);
          clearTimeout(stopTimer);
          videoTrack?.removeEventListener("ended", stopAfterShareEnds);
          reject(getMediaRecorderError(event));
        };

        mediaRecorder.start();

        // Stop after duration
        stopTimer = setTimeout(() => {
          if (mediaRecorder.state !== "inactive") mediaRecorder.stop();
        }, durationMs);
      }),
    [editVideoExportCounter]
  );

  const downloadBlob = useCallback((blob, filename, extension) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.${extension}`;
    document.body.appendChild(a);
    a.click();

    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 100);
  }, []);

  // Record video
  const recordVideo = useCallback(
    async (containerId, pageType, profileId = "720p", formatId = "mp4") => {
      const useServerRenderer = featureFlags.videoExport === true;
      if (useServerRenderer) {
        editIsVideoLoading(true);
        try {
          if (!window.__saveScene) {
            throw new Error("Save-scene bridge is not available");
          }
          const duration = Number(videoDuration);
          if (!Number.isFinite(duration) || duration < 0.1 || duration > 30) {
            throw new Error("Video duration must be a number between 0.1 and 30 seconds");
          }
          const profile = getVideoExportProfile(profileId);
          const format = getVideoExportFormat(formatId);
          const container = document.querySelector(`#${CSS.escape(containerId)}`);
          if (!container) throw new Error(`Export container not found: ${containerId}`);
          const surface = container.closest(".template-render-surface");
          if (!surface) {
            throw new Error("Video export container is outside a template render surface");
          }
          const sourceSize = {
            width: Number(surface.dataset.templateWidth),
            height: Number(surface.dataset.templateHeight),
          };
          const output = fitVideoExportProfile(sourceSize, profile);
          const sourceWarnings = getVideoExportSourceWarnings(container, output);
          if (
            sourceWarnings.length > 0 &&
            !window.confirm(
              `Some source media will be enlarged and may look soft:\n\n${sourceWarnings.join("\n")}\n\nExport anyway?`
            )
          ) {
            return;
          }
          const saveResult = await window.__saveScene({navigate: false});
          if (saveResult?.blocked) return;
          if (!saveResult?.scene_id) throw new Error("Scene save did not return a scene ID");

          // A page that closes or reloads cannot download the video. Cancel
          // its job, so the renderer starts the next export at once.
          let jobId = null;
          const cancelJobOnPageHide = () => {
            // request() logs a failure; the page closes, so nothing else can be done.
            if (jobId) API.cancelVideoRender(jobId).catch(() => {});
          };
          window.addEventListener("pagehide", cancelJobOnPageHide);
          let videoBlob;
          try {
            videoBlob = await API.renderVideo({
              sceneId: saveResult.scene_id,
              templateId: containerId,
              profile: profile.id,
              format: format.id,
              duration,
              onStatus: (status) => {
                jobId = status.id;
                setServerVideoStatus(status);
              },
            });
          } finally {
            window.removeEventListener("pagehide", cancelJobOnPageHide);
          }
          const filename = generateFilename(
            containerId,
            pageType,
            `${output.width}x${output.height}`
          );
          downloadBlob(videoBlob, filename, format.extension);
          reportExportCompleted("video", "server");
        } catch (error) {
          reportExportFailure("video", error);
        } finally {
          editIsVideoLoading(false);
          editVideoExportCounter(0, "video");
          setServerVideoStatus(null);
        }
        return;
      }

      try {
        const captureData = await startCapture(containerId, pageType, "video");
        if (!captureData) return;

        const {stream, filename} = captureData;
        const ms = videoDuration * 1000;
        const videoBlob = await createVideoFromStream(stream, ms, "video");
        await stopCapture("video");
        if (captureCancelledRef.current) return;
        downloadBlob(videoBlob, filename, "webm");
        reportExportCompleted("video");
        console.log("✅ Video saved!");
      } catch (error) {
        await stopCapture("video");
        reportExportFailure("video", error);
      }
    },
    [
      startCapture,
      stopCapture,
      videoDuration,
      createVideoFromStream,
      downloadBlob,
      reportExportFailure,
      reportExportCompleted,
      featureFlags.videoExport,
      editIsVideoLoading,
      editVideoExportCounter,
      generateFilename,
    ]
  );

  // Record GIF
  const recordGif = useCallback(
    async (containerId, pageType) => {
      editIsGifLoading(true);
      try {
        const captureData = await startCapture(containerId, pageType, "pregif");
        if (!captureData) return;

        const {reader, filename} = captureData;
        const ms = videoDuration * 1000;
        console.log("GIF duration:", videoDuration, "seconds");

        // Capture frames for specified duration
        await captureFrames(reader, ms);

        // Stop capture
        const frames = await stopCapture("pregif");

        if (captureCancelledRef.current) {
          return;
        }

        if (frames.length === 0) {
          throw new Error("No frames captured");
        }

        // Create GIF from frames
        const gifBlob = await createGifFromFrames(frames);

        // Download GIF
        downloadBlob(gifBlob, filename, "gif");
        reportExportCompleted("GIF");
        console.log("✅ GIF saved!");
      } catch (error) {
        await stopCapture("pregif");
        reportExportFailure("GIF", error);
      } finally {
        finishGifCapture(videoFramesRef, editIsGifLoading);
      }
    },
    [
      startCapture,
      stopCapture,
      captureFrames,
      createGifFromFrames,
      videoDuration,
      downloadBlob,
      editIsGifLoading,
      reportExportFailure,
      reportExportCompleted,
    ]
  );

  return {
    recordVideo,
    recordGif,
    serverVideoStatus,
  };
}
